import euscpWorkerUrl from "./euscp/euscp.worker.ex.js?url"
import type { EndUserSettings } from "./euscp/euscp"
import EUSignLibraryAdapter from "./EUSignLibraryAdapter"
import { loadEuscpScript } from "./loadEuscpScript"

// Same CA data as the hardware loader (EUSignLibraryLoader) — both library
// types validate certificates against the same ЄДРТЗ CA chain. Unlike the SW
// loader though, this one runs entirely in-browser, so it's actually subject
// to CORS: CAs whose CAs.json entry has directAccess:false (e.g.
// ca.monobank.ua) refuse direct browser calls, and the key-file container
// itself usually has no embedded certificate to fall back on — the library
// has to fetch it from the CA via CMP, which fails outright without a relay.
// `/euscp-proxy` is the dev-only Vite middleware (vite-euscp-proxy.ts) that
// relays those calls, a straight port of the vendor's own reference
// Modules/Server/ProxyHandler.js.
const settings: EndUserSettings = {
  language: "uk",
  encoding: "utf-8",
  httpProxyServiceURL: "/euscp-proxy",
  directAccess: true,
  CAs: "/euscp/Data/CAs.json",
  CACertificates: "/euscp/Data/CACertificates.p7b",
}

/**
 * Dev-only counterpart to EUSignLibraryLoader: loads a JavaScript-type
 * EndUser instance (Test/JS/main.js's `euSignFile`) that reads private keys
 * straight from an uploaded key file in-browser, instead of talking to the
 * native ІІТ sign agent. Lets КЕП sign-in be exercised locally without
 * installing that agent or owning a hardware token.
 */
export default class EUSignFileLibraryLoader {
  async load(): Promise<EUSignLibraryAdapter> {
    await loadEuscpScript()

    // Unlike the SW (hardware) loader, the JS library type has no in-page
    // fallback for running its crypto work off the main thread — it needs
    // an actual dedicated Worker script. euscp.js's own top-level code
    // isn't that script (loading it as a Worker just hangs, since it never
    // wires up `self.onmessage`); euscp.worker.ex.js is the real one, same
    // as Test/JS/main.js's `euSignFile`.
    const library = new window.EndUser(
      euscpWorkerUrl,
      window.EndUserConstants.EndUserLibraryType.JS
    )

    const isInitialized = await library.IsInitialized()
    if (!isInitialized) {
      await library.Initialize(settings)
    }

    return new EUSignLibraryAdapter(library)
  }
}
